import React from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Roles } from '../utils/enums/roles.enum';
import { LayoutType } from '../utils/enums/layout-type.enum';
import Login from '../pages/Login/Login';
import ResetPassword from '../pages/ResetPassword/ResetPassword';
import ForgotPassword from '../pages/ForgotPassword/ForgotPassword';
import PrivateRoute from '../components/PrivateRouter/PrivateRouter';
import NotFound from '../pages/NotFound/NotFound';
import {
  ACCOUNT_ACTIVATION_SUCCESS,
  ACCOUNT_LOCKED_ROUTE,
  CONTACT_US_ROUTE,
  CREATE_PIN_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  GEM_FULL_DETAILS_ROUTE,
  GEM_GPT_ROUTE,
  GEM_RECORD_ROUTE,
  GEM_RECORD_ROUTE_FAV,
  INVITATIONS_ROUTE,
  INVITE_NCC_USER_ROUTE,
  INVITE_USER_ROUTE,
  LOGIN_ROUTE,
  MANAGE_SUBSCRIPTION,
  MY_GEM_FAV_ROUTE,
  MY_GEM_VAULT_ROUTE,
  MY_PROFILE_ROUTE,
  NCC_SUBORDINATES_SIGN_IN_ROUTE,
  PAYMENTS_ROUTE,
  POROLIS_GEM_BANK_ROUTE,
  RESET_PASSWORD_ROUTE,
  SIGN_UP_ROUTE,
  SUBORDINATES_ROUTE,
  SUBORDINATES_SIGN_IN_ROUTE,
  TERMS_AND_CONDITIONS_ROUTE,
} from '../utils/routes';
import SignUp from '../pages/SignUp/SignUp';
import ContactUs from '../components/SignUp/ContactUs/ContactUs';
import MyGemVault from '../pages/MyGemVault/MyGemVault';
import GemRecord from '../pages/MyGemVault/GemRecord/GemRecord';
import AccountActivationSuccess from '../pages/AccountActivationSuccess/AccountActivationSuccess';
import Payments from '../pages/Payments/Payments';
import AccountLockedPage from '../pages/AccountLockedPage/AccountLockedPage';
import UpdateSubscription from '../pages/UpdateSubscription/UpdateSubscription';
import MyGemFav from '../pages/MyGemFav/MyGemFav';
import GemBank from '../pages/GemBank/GemBank';
import Subordinates from '../pages/Subordinates/Subordinates';
import SubordinateSignUp from '../pages/Subordinates/SignUp/SignUp';
import TermsAndConditions from '../pages/Terms&Conditions/Terms&Conditions';
import Invite from '../pages/Invite/Invite';
import CreatePin from '../pages/Subordinates/CreatePin/CreatePin';
import MyProfile from '../pages/MyProfile/MyProfile';
import GemGPT from '../pages/GemGPT/GemGPT';
import { Permission } from '../utils/enums/permission.enum';
import AppLayout from '../components/V1/Layouts/AppLayout/AppLayout';
import NCCSubordinateSignUp from '../pages/Subordinates/NCCSignUp/NCCSignUp';
import UpdateSubscriptionWrapper from '../pages/UpdateSubscription/UpdateSubscriptionWrapper';
import PaymentsWrapper from '../pages/Payments/PaymentsWrapper';

const routes = (isLoggedIn: boolean, role: Roles, permissions: Permission[]): RouteObject[] => [
  {
    path: '/',
    element: (
      <PrivateRoute
        userRole={role}
        routerRoles={[Roles.SAAS_ADMIN, Roles.SAAS_USER, Roles.NCC_USER]}
        DashboardLayout={LayoutType.BLANKLAYOUTV1}
      />
    ),
    children: [
      {
        path: '/',
        element: <Navigate to={CREATE_PIN_ROUTE} />,
      },
      {
        path: CREATE_PIN_ROUTE,
        element: (
          <AppLayout layout={LayoutType.BLANKLAYOUTV1}>
            <CreatePin />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: MY_GEM_VAULT_ROUTE,
    element: (
      <PrivateRoute
        userRole={role}
        routerRoles={[Roles.SAAS_ADMIN, Roles.SAAS_USER, Roles.NCC_USER]}
        permissions={permissions}
        route={MY_GEM_VAULT_ROUTE}
        DashboardLayout={LayoutType.DASHBOARDV1}
      />
    ),
    children: [
      {
        path: MY_GEM_VAULT_ROUTE,
        element: (
          <AppLayout layout={LayoutType.DASHBOARDV1}>
            <MyGemVault />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: ACCOUNT_LOCKED_ROUTE,
    element: isLoggedIn ? <Outlet /> : <Navigate to="/" />,
    children: [
      {
        path: ACCOUNT_LOCKED_ROUTE,
        element: (
          <AppLayout layout={LayoutType.BLANKLAYOUTV1}>
            <AccountLockedPage />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: LOGIN_ROUTE,
    element: !isLoggedIn ? <Outlet /> : <Navigate to="/" />,
    children: [
      {
        path: LOGIN_ROUTE,
        element: (
          <AppLayout layout={LayoutType.BLANKLAYOUTV1}>
            <Login />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: RESET_PASSWORD_ROUTE,
    element: !isLoggedIn ? <Outlet /> : <Navigate to="/" />,
    children: [
      {
        path: RESET_PASSWORD_ROUTE,
        element: (
          <AppLayout layout={LayoutType.BLANKLAYOUTV1}>
            <ResetPassword />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: FORGOT_PASSWORD_ROUTE,
    element: !isLoggedIn ? <Outlet /> : <Navigate to="/" />,
    children: [
      {
        path: FORGOT_PASSWORD_ROUTE,
        element: (
          <AppLayout layout={LayoutType.BLANKLAYOUTV1}>
            <ForgotPassword />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: SIGN_UP_ROUTE,
    element: !isLoggedIn ? <Outlet /> : <Navigate to="/" />,
    children: [
      {
        path: SIGN_UP_ROUTE,
        element: (
          <AppLayout layout={LayoutType.BLANKLAYOUTV1} backgroundColor="#f0f0f0">
            <SignUp />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: INVITE_USER_ROUTE,
    element: !isLoggedIn ? <Outlet /> : <Navigate to="/" />,
    children: [
      {
        path: INVITE_USER_ROUTE,
        element: <Invite />,
      },
    ],
  },
  {
    path: INVITE_NCC_USER_ROUTE,
    element: !isLoggedIn ? <Outlet /> : <Navigate to="/" />,
    children: [
      {
        path: INVITE_NCC_USER_ROUTE,
        element: <Invite />,
      },
    ],
  },
  {
    path: CONTACT_US_ROUTE,
    children: [
      {
        path: CONTACT_US_ROUTE,
        element: (
          <AppLayout layout={LayoutType.BLANKLAYOUTV1} backgroundColor="#f0f0f0">
            <ContactUs />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: POROLIS_GEM_BANK_ROUTE,
    element: (
      <PrivateRoute
        permissions={permissions}
        route={POROLIS_GEM_BANK_ROUTE}
        userRole={role}
        routerRoles={[Roles.SAAS_ADMIN, Roles.SAAS_USER, Roles.NCC_USER]}
        DashboardLayout={LayoutType.DASHBOARDV1}
      />
    ),
    children: [
      {
        path: POROLIS_GEM_BANK_ROUTE,
        element: (
          <AppLayout layout={LayoutType.DASHBOARDV1}>
            <GemBank />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: GEM_FULL_DETAILS_ROUTE,
    element: (
      <PrivateRoute
        userRole={role}
        routerRoles={[Roles.SAAS_ADMIN, Roles.SAAS_USER, Roles.NCC_USER]}
        DashboardLayout={LayoutType.DASHBOARDV1}
      />
    ),
    children: [
      {
        path: GEM_FULL_DETAILS_ROUTE,
        element: (
          <AppLayout layout={LayoutType.DASHBOARDV1}>
            <GemRecord />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: GEM_RECORD_ROUTE,
    element: (
      <PrivateRoute
        userRole={role}
        routerRoles={[Roles.SAAS_ADMIN, Roles.SAAS_USER, Roles.NCC_USER]}
        DashboardLayout={LayoutType.DASHBOARDV1}
      />
    ),
    children: [
      {
        path: GEM_RECORD_ROUTE,
        element: (
          <AppLayout layout={LayoutType.DASHBOARDV1}>
            <GemRecord />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: GEM_RECORD_ROUTE_FAV,
    element: (
      <PrivateRoute
        userRole={role}
        routerRoles={[Roles.SAAS_ADMIN, Roles.SAAS_USER, Roles.NCC_USER]}
        DashboardLayout={LayoutType.DASHBOARDV1}
      />
    ),
    children: [
      {
        path: GEM_RECORD_ROUTE_FAV,
        element: (
          <AppLayout layout={LayoutType.DASHBOARDV1}>
            <GemRecord />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: ACCOUNT_ACTIVATION_SUCCESS,
    children: [
      {
        path: ACCOUNT_ACTIVATION_SUCCESS,
        element: (
          <AppLayout layout={LayoutType.BLANKLAYOUTV1} backgroundColor="#f0f0f0">
            <AccountActivationSuccess />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: '/',
    element: <PrivateRoute userRole={role} routerRoles={[Roles.SAAS_ADMIN]} DashboardLayout={LayoutType.DEFAULTV1} />,
    children: [
      {
        path: '/',
        element: <Navigate to={PAYMENTS_ROUTE} />,
      },
      {
        path: PAYMENTS_ROUTE,
        element: <PaymentsWrapper />,
      },
    ],
  },
  {
    path: '/',
    element: (
      <PrivateRoute
        userRole={role}
        routerRoles={[Roles.SAAS_ADMIN, Roles.SAAS_USER, Roles.NCC_USER]}
        permissions={permissions}
        DashboardLayout={LayoutType.DASHBOARDV1}
        route={MY_GEM_FAV_ROUTE}
      />
    ),
    children: [
      {
        path: '/',
        element: <Navigate to={MY_GEM_FAV_ROUTE} />,
      },
      {
        path: MY_GEM_FAV_ROUTE,
        element: (
          <AppLayout layout={LayoutType.DASHBOARDV1}>
            <MyGemFav />
          </AppLayout>
        ),
      },
    ],
  },

  {
    path: '/',
    element: (
      <PrivateRoute
        userRole={role}
        routerRoles={[Roles.SAAS_ADMIN, Roles.SAAS_USER, Roles.NCC_USER]}
        permissions={permissions}
        route={MANAGE_SUBSCRIPTION}
        DashboardLayout={LayoutType.DEFAULTV1}
      />
    ),
    children: [
      {
        path: '/',
        element: <Navigate to={MANAGE_SUBSCRIPTION} />,
      },
      {
        path: MANAGE_SUBSCRIPTION,
        element: <UpdateSubscriptionWrapper />,
      },
    ],
  },
  {
    path: '/',
    element: <PrivateRoute userRole={role} routerRoles={[Roles.SAAS_ADMIN]} DashboardLayout={LayoutType.DEFAULTV1} />,
    children: [
      {
        path: '/',
        element: <Navigate to={SUBORDINATES_ROUTE} />,
      },
      {
        path: SUBORDINATES_ROUTE,
        element: (
          <AppLayout layout={LayoutType.DEFAULTV1}>
            <Subordinates />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: GEM_GPT_ROUTE,
    element: (
      <PrivateRoute
        permissions={permissions}
        route={GEM_GPT_ROUTE}
        userRole={role}
        routerRoles={[Roles.SAAS_ADMIN, Roles.SAAS_USER, Roles.NCC_USER]}
        DashboardLayout={LayoutType.DEFAULTV1}
      />
    ),
    children: [
      {
        path: GEM_GPT_ROUTE,
        element: (
          <AppLayout layout={LayoutType.DEFAULTV1}>
            <GemGPT />
          </AppLayout>
        ),
      },
    ],
  },

  {
    path: INVITATIONS_ROUTE,
    element: <PrivateRoute userRole={role} routerRoles={[Roles.SAAS_ADMIN]} DashboardLayout={LayoutType.DEFAULTV1} />,
    children: [
      {
        path: INVITATIONS_ROUTE,
        element: (
          <AppLayout layout={LayoutType.DEFAULTV1}>
            <Subordinates />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: MY_PROFILE_ROUTE,
    element: (
      <PrivateRoute
        userRole={role}
        routerRoles={[Roles.SAAS_ADMIN, Roles.SAAS_USER, Roles.NCC_USER]}
        DashboardLayout={LayoutType.DEFAULTV1}
      />
    ),
    children: [
      {
        path: MY_PROFILE_ROUTE,
        element: (
          <AppLayout layout={LayoutType.DEFAULTV1}>
            <MyProfile />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: SUBORDINATES_SIGN_IN_ROUTE,
    element: !isLoggedIn ? <Outlet /> : <Navigate to="/" />,
    children: [
      {
        path: SUBORDINATES_SIGN_IN_ROUTE,
        element: (
          <AppLayout layout={LayoutType.BLANKLAYOUTV1}>
            <SubordinateSignUp />
          </AppLayout>
        ),
      },
    ],
  },
  {
    path: NCC_SUBORDINATES_SIGN_IN_ROUTE,
    element: !isLoggedIn ? <Outlet /> : <Navigate to="/" />,
    children: [
      {
        path: NCC_SUBORDINATES_SIGN_IN_ROUTE,
        element: (
          <AppLayout layout={LayoutType.BLANKLAYOUTV1}>
            <NCCSubordinateSignUp />
          </AppLayout>
        ),
      },
    ],
  },
  { path: TERMS_AND_CONDITIONS_ROUTE, element: <TermsAndConditions /> },
  { path: '*', element: <NotFound /> },
];

export default routes;
